import { useMutation, useQuery } from '@tanstack/react-query';
import { z } from 'zod';
import { api } from '@/lib/api-client';

const clienteBuscaSchema = z.object({
  cpf: z.string(),
  nome: z.string(),
  municipio: z.string().nullish(),
  uf: z.string().nullish(),
});
export type ClienteBusca = z.infer<typeof clienteBuscaSchema>;

const imovelResumoSchema = z.object({
  id: z.number(),
  denominacao: z.string().nullish(),
  matricula: z.string().nullish(),
  municipio: z.string().nullish(),
  uf: z.string().nullish(),
  area_total_ha: z.coerce.number().nullish(),
});
export type ImovelResumo = z.infer<typeof imovelResumoSchema>;

const SOMENTE_DIGITOS = /\D/g;
export const apenasDigitos = (v: string) => v.replace(SOMENTE_DIGITOS, '');

/**
 * Busca por nome OU CPF. O back filtra nome por ILIKE e cpf por prefixo dos
 * dígitos; mandar os dois vazios devolveria a base inteira, então o hook só
 * dispara com 2+ caracteres.
 */
export function useBuscarClientes(termo: string) {
  const busca = termo.trim();
  const digitos = apenasDigitos(busca);
  const porCpf = digitos.length >= 2 && digitos.length >= busca.length - 3;
  return useQuery({
    queryKey: ['ambiental', 'busca-cliente', busca],
    enabled: busca.length >= 2,
    staleTime: 30_000,
    queryFn: () =>
      api(`/api/v2/clientes?${porCpf ? `cpf=${encodeURIComponent(digitos)}` : `nome=${encodeURIComponent(busca)}`}`, {
        schema: z.array(clienteBuscaSchema.passthrough()),
      }),
  });
}

export function useImoveisDoCliente(cpf: string | null) {
  return useQuery({
    queryKey: ['ambiental', 'imoveis-cliente', cpf],
    enabled: !!cpf,
    staleTime: 30_000,
    queryFn: () =>
      api(`/api/v2/imoveis?cpf=${encodeURIComponent(cpf as string)}`, {
        schema: z.array(imovelResumoSchema.passthrough()),
      }),
  });
}

/**
 * POST /clientes é UPSERT: se o CPF já existir, ele sobrescreve o cadastro real
 * (já aconteceu em produção). Por isso este hook consulta antes e recusa CPF
 * existente em vez de gravar por cima — quem já está na base entra pelo fluxo
 * "Cliente ativo".
 */
export function useCriarClienteNovo() {
  return useMutation({
    mutationFn: async (input: {
      cpf: string;
      nome: string;
      telefone?: string | null;
      municipio?: string | null;
      uf?: string | null;
    }) => {
      const existente = await api(`/api/v2/clientes/${encodeURIComponent(input.cpf)}`, {
        schema: z.object({ nome: z.string() }).passthrough(),
      }).catch(() => null);
      if (existente) {
        throw new Error(
          `Esse CPF já está cadastrado como "${existente.nome}". Use "Cliente ativo" para não sobrescrever o cadastro.`,
        );
      }
      return api('/api/v2/clientes', {
        method: 'POST',
        body: input,
        schema: z.object({ cpf: z.string(), nome: z.string() }).passthrough(),
      });
    },
  });
}
